/**
 * Auth bridge — exposes the cognito-passwordless library's hook-only token
 * accessor (`useAuth().getValidToken`) to non-React modules such as the axios
 * interceptors in `services/api.ts` (via `tokenResolver.ts`).
 *
 * WHY: `getValidToken()` refreshes the SSO session when the access token is
 * near/after expiry. Without this bridge, `tokenResolver` reads the raw
 * `bdance_oauth_tokens` store directly and cannot trigger a refresh, so an
 * expired token forces a hard logout mid-session (losing unsaved work).
 *
 * The provider is registered by `<AuthBridge />` (mounted under AuthProvider)
 * and cleared on unmount.
 *
 * NOTE: This participates in the authentication flow — changes here require
 * Security/human review per the project constitution (§13/§14).
 */
type ValidTokenGetter = () => Promise<string | null>;

let validTokenGetter: ValidTokenGetter | null = null;

/** Register the library's `getValidToken` accessor (called from a React effect). */
export function registerValidTokenGetter(fn: ValidTokenGetter | null): void {
  validTokenGetter = fn;
}

/**
 * Get a valid access token from the auth provider, refreshing if needed.
 * Returns null if the provider is not registered yet or refresh fails.
 */
export async function getValidTokenFromProvider(): Promise<string | null> {
  if (!validTokenGetter) return null;
  try {
    return await validTokenGetter();
  } catch {
    return null;
  }
}
